// PRD_ContributionBasisReport C17 — the contribution-basis wording shared by the
// payroll wizard's Deductions step and the Contribution Basis report, so one
// cause reads the same on both screens. Moved verbatim from DeductionsStep.js.
import { format } from 'date-fns';
import { parsePeriodToDate } from '../utils/periodDate';

// 'YYYY-MM' → "August 2026" (PRD_SssContributionBasis C22). Short "Aug 2026"
// labels use formatMonthLabel instead.
export const longMonthLabel = (key) => {
  const [y, m] = key.split('-');
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(Number(y), Number(m) - 1, 1)
  );
};

// PRD_SssBasisPieceRateAndSuppressionReasons P6/P7 — one sentence per cause,
// exact strings, decided server-side (the client derives nothing). The
// operator's next action differs for each: nothing / check Time Tracking / correct
// and expected / the one that looks like a pay bug.
const SUPPRESSION_REASON = {
  'not-employed': (m) => `Not employed during ${m} — no contribution is due.`,
  'no-records': (m) => `No time records for ${m} — check Time Tracking for that month.`,
  'no-earnings': (m) => `No earnings recorded for ${m}.`,
  'net-zero': (m) => `${m} earnings net to zero after undertime and absences.`,
  // H12: only this one names an agency. Parameterised so the PhilHealth column
  // cannot tell an operator to go publish an SSS table.
  'no-table': (m, agency) => `No ${agency} contribution table is published for ${m}.`,
  // PRD_ContributionsStartDate C4: names the date, not the month.
  'before-contributions-start': (m, agency, csd) => `${contributionsStartLabel(csd)}.`,
};

// The default arm is the no-earnings string, so a draft written by an older
// build (or an unrecognised value) never renders a blank line.
export const suppressionReason = (code, monthKey, agency, contributionsStartDate) =>
  (SUPPRESSION_REASON[code] || SUPPRESSION_REASON['no-earnings'])(
    longMonthLabel(monthKey),
    agency,
    contributionsStartDate
  );

// PRD_ContributionsStartDate C7: the Deductions marker ("Contributions start
// Oct 16, 2026", no full stop). parsePeriodToDate, never new Date(ymd) (UTC).
export const contributionsStartLabel = (ymd) =>
  `Contributions start ${format(parsePeriodToDate(ymd), 'MMM d, yyyy')}`;

// PRD_ContributionsStartDate C5 — the one client copy of the saved-amount test:
// ₱0 in any month before the one containing the contributions start. Mirrored
// server-side in computeContributionSchedule's stored().
export const isBeforeContributionsStart = (employment, monthKey) =>
  Boolean(employment?.contributionsStartDate) &&
  monthKey < employment.contributionsStartDate.slice(0, 7);

// C9: keyed off the matched row's MSC, never the basis — ₱5,100 and ₱3,000 get
// the same line. The floor figure is C22's locked string; the ceiling line
// carries no figure — mscMax is a platform constant the client cannot read.
// H12: SSS keeps C22's locked strings verbatim. PhilHealth gets FIGURE-FREE
// equivalents — its floor and ceiling are published table parameters, so a peso
// amount hard-coded here would go stale the next time PhilHealth revises.
export const boundNote = (atBound, agency) => {
  if (atBound === 'floor') {
    return agency === 'SSS' ? 'Minimum salary credit ₱5,000.00 applies' : 'Income floor applies';
  }
  if (atBound === 'ceiling') {
    return agency === 'SSS' ? 'Maximum salary credit applies' : 'Income ceiling applies';
  }
  return null;
};
